import { Grid } from '@mui/material';
import PmiTextField from 'shell/components/shared/text-field';
import ListGender from 'shell/components/shared/gender';
import { DatePicker, TimePicker } from '@mui/lab';
import { memo, useMemo } from 'react';
import { FormikErrors, FormikTouched } from 'formik';
import { DocumentBirthFormVM } from 'civil-status/view-models/document-birth-form';
import MatricolaList from '../shared/matricola-list';
import { PageMode } from 'civil-status/view-models/page-mode';

interface DocumentBirthFormProps {
  values?: DocumentBirthFormVM;
  errors?: FormikErrors<DocumentBirthFormVM>;
  touched?: FormikTouched<DocumentBirthFormVM>;
  onChange: (
    key: keyof DocumentBirthFormVM,
    value: DocumentBirthFormVM[keyof DocumentBirthFormVM]
  ) => void;
  onChanges: (values: DocumentBirthFormVM) => void;
  mode: PageMode;
}

const DocumentBirthFormComponent = ({
  values,
  touched,
  errors,
  mode,
  onChange,
  onChanges,
}: DocumentBirthFormProps): React.ReactElement => {
  const disabled = useMemo(() => {
    return mode === PageMode.Detail;
  }, [mode]);

  return (
    <>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <MatricolaList
            isSmallSize
            aria-label="Matricola"
            label="Matricola"
            id="matricola"
            disabled={disabled}
            surname={values?.surname || ''}
            name={values?.name || ''}
            dateBirth={values?.birthday || null}
            value={values?.matricola || ''}
            onChanges={(value) => onChanges(value)}
            error={touched?.matricola && Boolean(errors?.matricola)}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <PmiTextField
            aria-label="Cognome"
            label="Cognome"
            id="surname"
            name="surname"
            fullWidth
            disabled={disabled}
            value={values?.surname || ''}
            onChange={(event) => onChange('surname', event?.target.value)}
            error={touched?.surname && Boolean(errors?.surname)}
            helperText={errors?.surname}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <PmiTextField
            aria-label="Nome"
            label="Nome"
            id="name"
            name="name"
            fullWidth
            disabled={disabled}
            value={values?.name || ''}
            onChange={(event) => onChange('name', event?.target.value)}
            error={touched?.name && Boolean(errors?.name)}
            helperText={errors?.name}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <ListGender
            value={values?.gender || ''}
            onChange={(event) => onChange('gender', event?.target.value)}
            error={touched?.gender && Boolean(errors?.gender)}
            disabled={disabled}
            isSmallSize
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <DatePicker
            label="Data nascita"
            aria-label="Data nascita"
            value={values?.birthday || null}
            disabled={disabled}
            onChange={(value) => onChange('birthday', value)}
            renderInput={(params) => (
              <PmiTextField
                id="birthday"
                name="birthday"
                {...params}
                fullWidth
                error={touched?.birthday && Boolean(errors?.birthday)}
                helperText={errors?.birthday}
              />
            )}
          />
        </Grid>
      </Grid>
      {disabled && !values?.time ? undefined : (
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={12} style={{ paddingTop: '10px' }}>
            <TimePicker
              label="Ora nascita"
              value={values?.time || null}
              disabled={disabled}
              onChange={(value) => onChange('time', value)}
              renderInput={(params) => (
                <PmiTextField
                  id="time"
                  name="time"
                  {...params}
                  fullWidth
                  error={touched?.time && Boolean(errors?.time)}
                  helperText={errors?.time}
                />
              )}
            />
          </Grid>
        </Grid>
      )}
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <PmiTextField
            aria-label="Comune nascita"
            label="Comune nascita"
            id="municipalityBirth"
            name="municipalityBirth"
            fullWidth
            disabled={disabled}
            value={values?.municipalityBirth || ''}
            onKeyDown={(event) => {
              if (event.code === 'F2') {
                event.preventDefault();
                event.stopPropagation();
                onChange('municipalityBirth', 'MILANO');
                return;
              }
            }}
            onChange={(event) => {
              onChange('municipalityBirth', event?.target.value);
            }}
            error={touched?.municipalityBirth && Boolean(errors?.municipalityBirth)}
            helperText={errors?.municipalityBirth}
          />
          <span>F2 per Milano</span>
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <PmiTextField
            aria-label="Padre - Cognome"
            label="Padre - Cognome"
            id="surnameFather"
            name="surnameFather"
            fullWidth
            disabled={disabled}
            value={values?.surnameFather || ''}
            onChange={(event) => onChange('surnameFather', event?.target.value)}
            error={touched?.surnameFather && Boolean(errors?.surnameFather)}
            helperText={errors?.surnameFather}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <PmiTextField
            aria-label="Padre - Nome"
            label="Padre - Nome"
            id="nameFather"
            name="nameFather"
            fullWidth
            disabled={disabled}
            value={values?.nameFather || ''}
            onChange={(event) => onChange('nameFather', event?.target.value)}
            error={touched?.nameFather && Boolean(errors?.nameFather)}
            helperText={errors?.nameFather}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <PmiTextField
            aria-label="Madre - Cognome"
            label="Madre - Cognome"
            id="surnameMother"
            name="surnameMother"
            fullWidth
            disabled={disabled}
            value={values?.surnameMother || ''}
            onChange={(event) => onChange('surnameMother', event?.target.value)}
            error={touched?.surnameMother && Boolean(errors?.surnameMother)}
            helperText={errors?.surnameMother}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <PmiTextField
            aria-label="Madre - Nome"
            label="Madre - Nome"
            id="nameMother"
            name="nameMother"
            fullWidth
            disabled={disabled}
            value={values?.nameMother || ''}
            onChange={(event) => onChange('nameMother', event?.target.value)}
            error={touched?.nameMother && Boolean(errors?.nameMother)}
            helperText={errors?.nameMother}
          />
        </Grid>
      </Grid>
    </>
  );
};

DocumentBirthFormComponent.displayName = 'Document Birth form component';

const DocumentBirthFormShared = memo(DocumentBirthFormComponent);

export default DocumentBirthFormShared;
